// Shared submit validation for operator create, send-intake, merchant
// intake, and the edit modal. Callers keep field-id prefixes and
// clear-on-fix / focus behavior; this module owns the rules.
//
// After Step 1.5 (noValidate), this is what actually blocks a bad submit.

import {
  allowedTitlesForOwnershipType,
  businessStartDateRules,
  dateFieldError,
  detectFakeEIN,
  detectFakeSSN,
  EMAIL_RE,
  isURL,
  isValidPhone,
  isValidRoutingNumber,
  isValidState,
  OWNER_NAME_SINGLE_WORD_MESSAGE,
  ownerDobRules,
  ownerNameHasSpace,
  ROUTING_RE,
  SSN_RE,
  TAX_ID_RE,
} from "@/lib/formatters";
import { MCC_BY_CODE } from "@/lib/mcc";
import type { CardConnectEditAction } from "@/lib/types";
import {
  annualSecurityBundleFeeErrorMessage,
  annualSecurityBundleFeeInvalid,
  authFeeOverrideErrorMessage,
  authFeeOverrideInvalid,
  cloverSecurityAndTransarmorInvalidFields,
  earlyCancelFeeCapInvalidField,
  feeOverridesInvalidFields,
  fleetOverridesInvalidFields,
  icPlusPricingOverridesInvalidFields,
  ownerTitleMismatch,
  ownerTitleMismatchMessage,
  volumeTicketErrors,
  type AddressForm,
  type BankForm,
  type BusinessForm,
  type CloverSecurityAndTransarmorForm,
  type FeeOverridesForm,
  type FieldError,
  type FleetOverridesForm,
  type IcPlusPricingOverridesForm,
  type ModeForm,
  type OwnerForm,
  type VolumeDetailsForms,
} from "@/components/application";
import {
  findMissingAcceptanceLabels,
  mnspFeeFieldErrors,
  mnspServiceTypeErrors,
  type MnspEnrollmentFormState,
} from "@/components/MnspEnrollmentSection";

type AddressLike = {
  address1: string;
  city: string;
  stateCd: string;
  zip: string;
  countryCd?: string;
};

type BankLike = {
  accountNumber?: string | null;
  // Double-entry confirmation of accountNumber (issue #195). Optional so
  // historical/censored payloads without it don't fail typing; the match
  // check is skipped when the account is blank or a censored last-4.
  confirmAccountNumber?: string | null;
  routingNumber?: string | null;
  accountType: string;
  bankName: string;
};

const PIN_RE = /^\d{4,8}$/;
const PIN_MESSAGE = "Must be a 4-8 digit number.";
// Double-entry account-number match (issue #195). The account number is
// re-keyed into a confirmation field so a single mistyped digit is caught
// before boarding rather than routing deposits to the wrong account.
export const ACCOUNT_MISMATCH_MESSAGE = "Account numbers don't match.";
export const ACCOUNT_CONFIRM_REQUIRED_MESSAGE =
  "Re-enter the account number to confirm it.";
const OWNERSHIP_PCT_MESSAGE = "Ownership % must be between 1 and 100.";
const PHONE_MESSAGE =
  "Phone must be XXX-XXX-XXXX (area code can't start with 0/1).";
const COUNTRY_MESSAGE = "Country must be a 2-letter code (e.g. US).";
const MCC_LIST_MESSAGE = "Pick a merchant category from the list.";
const ECOM_WEBSITE_MESSAGE =
  "A website is required when e-commerce is part of the transaction mix.";

function push(errors: FieldError[], field: string, message: string) {
  errors.push({ field, message });
}

function countryCodeError(field: string, countryCd: string | undefined) {
  const c = (countryCd ?? "").trim();
  if (!c) return null;
  if (!/^[A-Za-z]{2}$/.test(c)) return { field, message: COUNTRY_MESSAGE };
  return null;
}

// A plain 5-digit ZIP only -- CardConnect's boarding API rejects a ZIP+4
// (more than 5 digits), so a stored/hydrated 9-digit value is flagged here
// too, not just blocked from fresh entry (FIELD_FORMAT_DEFAULTS.zip), since
// resubmitting it would fail at CardConnect regardless of what else changed.
const ZIP_DIGITS = 5;

// Digit count only, tolerant of a "-" or other stray formatting a pre-mask
// historical record might carry -- the live input only ever lets an
// operator/merchant type digits.
function zipDigitCount(zip: string): number {
  return zip.replace(/\D/g, "").length;
}

function zipLengthInvalid(zip: string): boolean {
  return zipDigitCount(zip) !== ZIP_DIGITS;
}

const ZIP_LENGTH_MESSAGE = `ZIP must be ${ZIP_DIGITS} digits.`;

function formatAddressIfFilled(
  errors: FieldError[],
  prefix: string,
  addr: AddressLike | undefined,
) {
  if (!addr) return;
  if (addr.stateCd.trim() && !isValidState(addr.stateCd))
    push(
      errors,
      `${prefix}.stateCd`,
      `Pick a valid state (got "${addr.stateCd}").`,
    );
  if (addr.zip.trim() && zipLengthInvalid(addr.zip))
    push(errors, `${prefix}.zip`, ZIP_LENGTH_MESSAGE);
  const countryErr = countryCodeError(`${prefix}.countryCd`, addr.countryCd);
  if (countryErr) errors.push(countryErr);
}

// Double-entry match for an account number (issue #195). Runs only when a
// real account number is being entered: skipped when the account is blank
// (its own required check owns that) and when it is a censored last-4
// placeholder (****1234) hydrated from storage and left untouched.
function accountConfirmError(
  errors: FieldError[],
  confirmField: string,
  account: string | null | undefined,
  confirm: string | null | undefined,
  opts?: { censored?: boolean },
) {
  const acct = (account ?? "").trim();
  if (opts?.censored) return;
  if (!acct || acct.startsWith("****")) return;
  const conf = (confirm ?? "").trim();
  if (!conf) {
    push(errors, confirmField, ACCOUNT_CONFIRM_REQUIRED_MESSAGE);
    return;
  }
  if (conf !== acct) push(errors, confirmField, ACCOUNT_MISMATCH_MESSAGE);
}

function formatRoutingIfFilled(
  errors: FieldError[],
  field: string,
  routing: string | null | undefined,
) {
  const raw = (routing ?? "").trim();
  if (!raw) return;
  if (!ROUTING_RE.test(raw)) {
    push(errors, field, "Routing number must be exactly 9 digits.");
  } else if (!isValidRoutingNumber(raw)) {
    push(
      errors,
      field,
      "That routing number's check digit is wrong — double-check the number on the check.",
    );
  }
}

function requireAddress(
  errors: FieldError[],
  prefix: string,
  addr: AddressLike,
  label: string,
) {
  if (!addr.address1.trim())
    push(errors, `${prefix}.address1`, `${label} street address is required.`);
  if (!addr.city.trim())
    push(errors, `${prefix}.city`, `${label} city is required.`);
  if (!addr.stateCd.trim())
    push(errors, `${prefix}.stateCd`, `${label} state is required.`);
  else if (!isValidState(addr.stateCd))
    push(
      errors,
      `${prefix}.stateCd`,
      `Pick a valid state (got "${addr.stateCd}").`,
    );
  if (!addr.zip.trim())
    push(errors, `${prefix}.zip`, `${label} ZIP is required.`);
  else if (zipLengthInvalid(addr.zip))
    push(errors, `${prefix}.zip`, `${label} ${ZIP_LENGTH_MESSAGE}`);
  const countryErr = countryCodeError(`${prefix}.countryCd`, addr.countryCd);
  if (countryErr) errors.push(countryErr);
}

function requireBank(
  errors: FieldError[],
  ids: {
    account: string;
    accountConfirm: string;
    routing: string;
    type: string;
    bank: string;
  },
  acct: BankLike,
  opts?: { routingCensored?: boolean; accountCensored?: boolean },
) {
  if (!acct.accountNumber?.trim())
    push(errors, ids.account, "Account number is required.");
  else
    accountConfirmError(
      errors,
      ids.accountConfirm,
      acct.accountNumber,
      acct.confirmAccountNumber,
      { censored: opts?.accountCensored },
    );
  if (!acct.routingNumber?.trim()) {
    push(errors, ids.routing, "Routing number is required.");
  } else if (!opts?.routingCensored) {
    if (!ROUTING_RE.test(acct.routingNumber)) {
      push(errors, ids.routing, "Routing number must be exactly 9 digits.");
    } else if (!isValidRoutingNumber(acct.routingNumber)) {
      push(
        errors,
        ids.routing,
        "That routing number's check digit is wrong — double-check the number on the check.",
      );
    }
  }
  if (!acct.accountType.trim())
    push(errors, ids.type, "Account type is required.");
  if (!acct.bankName.trim())
    push(errors, ids.bank, "Bank name is required.");
}

function percentOutOfRange(raw: string): boolean {
  const n = Number(String(raw).trim() || 0);
  return !Number.isFinite(n) || n < 0 || n > 100;
}

// An out-of-range bucket is attributed to the input that holds it, so the
// operator sees which box is wrong; "must total 100" has no single owner and
// stays on the group key. Reporting the sum while a bucket is out of range
// would just be noise on top of the real problem, so range wins.
function percentGroupErrors(
  errors: FieldError[],
  groupField: string,
  label: string,
  buckets: { field: string; value: string }[],
) {
  let outOfRange = false;
  for (const { field, value } of buckets) {
    if (percentOutOfRange(value)) {
      push(errors, field, "Must be between 0 and 100.");
      outOfRange = true;
    }
  }
  if (outOfRange) return;
  const sum = buckets.reduce((acc, b) => acc + Number(b.value), 0);
  if (sum !== 100)
    push(errors, groupField, `${label} must total 100 (currently ${sum}).`);
}

// Listed in the order the inputs render, so the roll-up banner and
// scroll-to-first-error follow the eye down the page.
function modeBuckets(mode: ModeForm) {
  return [
    { field: "modeOfTransaction.swipedPct", value: mode.swipedPct },
    { field: "modeOfTransaction.keyedPct", value: mode.keyedPct },
    { field: "modeOfTransaction.eCommercePct", value: mode.eCommercePct },
    { field: "modeOfTransaction.mailOrderPct", value: mode.mailOrderPct },
  ];
}

// The PIN identifies the salesperson to CardConnect; the server and the edit
// modal have always enforced 4-8 digits, create just never did.
function pinError(
  errors: FieldError[],
  field: string,
  pin: string,
  opts?: { required?: boolean },
) {
  const trimmed = pin.trim();
  if (!trimmed) {
    if (opts?.required) push(errors, field, PIN_MESSAGE);
    return;
  }
  if (!PIN_RE.test(trimmed)) push(errors, field, PIN_MESSAGE);
}

// 0 would say the signer owns none of the business, which CardConnect
// rejects; blank is fine (it's optional).
function ownershipPctError(errors: FieldError[], pct: string) {
  const trimmed = pct.trim();
  if (!trimmed) return;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 1 || n > 100)
    push(errors, "owner.ownershipPct", OWNERSHIP_PCT_MESSAGE);
}

function requireMcc(errors: FieldError[], mcc: string) {
  if (!mcc.trim()) {
    push(errors, "mcc", "Merchant category is required.");
  } else if (!MCC_BY_CODE[mcc.trim()]) {
    push(errors, "mcc", MCC_LIST_MESSAGE);
  }
}

function requireTaxId(
  errors: FieldError[],
  field: string,
  taxID: string | null | undefined,
  taxFilingMethod: string,
  opts?: { censored?: boolean; required?: boolean },
) {
  const raw = taxID ?? "";
  if (!raw.trim()) {
    if (opts?.required !== false)
      push(errors, field, "Tax ID is required.");
    return;
  }
  if (opts?.censored) return;
  const taxDigits = raw.replace(/\D/g, "");
  if (!TAX_ID_RE.test(taxDigits)) {
    push(errors, field, "Tax ID must be exactly 9 digits.");
    return;
  }
  const reason =
    taxFilingMethod === "EIN"
      ? detectFakeEIN(taxDigits)
      : detectFakeSSN(taxDigits);
  if (reason) push(errors, field, reason);
}

function requireSsn(
  errors: FieldError[],
  field: string,
  ssn: string | null | undefined,
  opts?: { censored?: boolean; required?: boolean },
) {
  const raw = ssn ?? "";
  if (!raw.trim()) {
    if (opts?.required !== false) push(errors, field, "Owner SSN is required.");
    return;
  }
  if (opts?.censored) return;
  if (!SSN_RE.test(raw)) {
    push(errors, field, field === "owner.ssn" ? "SSN must be XXX-XX-XXXX." : "Enter a valid SSN.");
    return;
  }
  const reason = detectFakeSSN(raw);
  if (reason) push(errors, field, reason);
}

function websiteErrors(
  errors: FieldError[],
  website: string,
  eCommercePct: string,
  requireWhenEcommerce: boolean,
) {
  const trimmed = website.trim();
  if (trimmed && !isURL.test(trimmed)) {
    push(errors, "website", "Enter a valid website URL.");
    return;
  }
  if (requireWhenEcommerce && Number(eCommercePct) > 0 && !trimmed) {
    push(errors, "website", ECOM_WEBSITE_MESSAGE);
  }
}

export function collectPricingOverrideErrors(input: {
  authFeeOverride: string;
  annualSecurityBundleFee: string;
  feeOverrides: FeeOverridesForm;
  icPlusPricingOverrides: IcPlusPricingOverridesForm;
  cloverSecurityAndTransarmor: CloverSecurityAndTransarmorForm;
  fleetOverrides: FleetOverridesForm;
  mnspForm: MnspEnrollmentFormState;
  mnspEnabled: boolean;
  terminalRental: boolean;
  // Business state + monthly volume drive the state ETF-cap check (mirrors the
  // server's boarding guard); the effective ETF is the override, else the tier
  // default resolved from the volume.
  stateCd: string;
  monthlyVolume: string;
  // Whether a missing MNSP register is an error. Off on the send-intake path
  // (and send-mode drafts): the recipient picks the register, and the intake
  // completion gate requires it. Defaults on.
  requireMnspServiceType?: boolean;
}): FieldError[] {
  const errs: FieldError[] = [];
  if (authFeeOverrideInvalid(input.authFeeOverride))
    errs.push({
      field: "fee-override.authFee",
      message: authFeeOverrideErrorMessage(input.authFeeOverride),
    });
  if (annualSecurityBundleFeeInvalid(input.annualSecurityBundleFee))
    errs.push({
      field: "fee-override.annualSecurityBundleFee",
      message: annualSecurityBundleFeeErrorMessage(input.annualSecurityBundleFee),
    });
  const etfCapErr = earlyCancelFeeCapInvalidField(
    input.feeOverrides,
    input.stateCd,
    input.monthlyVolume,
    "fee-override",
  );
  if (etfCapErr) errs.push(etfCapErr);
  errs.push(
    ...feeOverridesInvalidFields(input.feeOverrides, "fee-override"),
    ...icPlusPricingOverridesInvalidFields(
      input.icPlusPricingOverrides,
      "fee-override",
    ),
    ...cloverSecurityAndTransarmorInvalidFields(
      input.cloverSecurityAndTransarmor,
      "clover-security",
    ),
    ...fleetOverridesInvalidFields(input.fleetOverrides, "fleet-override"),
    ...mnspFeeFieldErrors(
      input.mnspForm,
      input.mnspEnabled,
      input.terminalRental,
    ),
    ...(input.requireMnspServiceType === false
      ? []
      : mnspServiceTypeErrors(input.mnspForm, input.mnspEnabled)),
  );
  return errs;
}

function salespersonPinError(errs: FieldError[], assignedToPIN: string): void {
  if (!/^\d{4,8}$/.test(assignedToPIN.trim()))
    errs.push({
      field: "assignedToPIN",
      message: "Salesperson's PIN must be a 4-8 digit number.",
    });
}

// The salesperson (email + PIN) is required when the operator fills the whole
// application, but optional on an intake send or a send-mode draft -- it can be
// assigned later -- where whatever is entered is still format-checked.
function salespersonErrors(
  errs: FieldError[],
  assignedTechEmail: string,
  assignedToPIN: string,
  required: boolean,
  suffix = "",
): void {
  const email = assignedTechEmail.trim();
  if (!email) {
    if (required)
      push(errs, "assigned-tech-email", `Salesperson's email is required${suffix}.`);
  } else if (!EMAIL_RE.test(email)) {
    push(errs, "assigned-tech-email", "Salesperson's email must be valid.");
  }
  if (required || assignedToPIN.trim()) salespersonPinError(errs, assignedToPIN);
}

export type SaveDraftValidationInput = {
  applicationPath: string;
  assignedTechEmail: string;
  assignedToPIN: string;
  biz: BusinessForm;
  bizAddr: AddressForm;
  owner: OwnerForm;
  vol: VolumeDetailsForms;
  authFeeOverride: string;
  annualSecurityBundleFee: string;
  feeOverrides: FeeOverridesForm;
  icPlusPricingOverrides: IcPlusPricingOverridesForm;
  cloverSecurityAndTransarmor: CloverSecurityAndTransarmorForm;
  fleetOverrides: FleetOverridesForm;
  mnspForm: MnspEnrollmentFormState;
  mnspEnrollment: boolean;
  terminalRental: boolean;
  // The intake recipient when the draft is saved from "Email an intake link"
  // (null/undefined from "Fill out & submit now"). A send-mode draft is held to
  // the send-intake bar: the salesperson and register are optional, and an
  // INTERNAL_SALES draft needs only the merchant email.
  intakeRecipientType?: string | null;
  intakeRecipientEmail?: string;
};

// "Save as draft": the only thing a draft strictly needs is a coherent
// identity (business name + contact email + the salesperson PIN/email the
// backend requires on every create path) and, since we're persisting them,
// no malformed pricing overrides. Owner title/type still have to pair if
// both are filled. Everything else -- banking, address, ownership, MCC,
// volume -- is optional and gets filled in later (by the operator via the
// edit modal, or by the merchant via an intake link from the detail page).
export function validateSaveDraft(input: SaveDraftValidationInput): FieldError[] {
  const errs: FieldError[] = [];
  const sendMode = Boolean(input.intakeRecipientType);
  const internalSales = input.intakeRecipientType === "INTERNAL_SALES";
  const businessName = input.biz.businessName.trim();
  const contactEmail = input.biz.contactEmail.trim();
  // INTERNAL_SALES: only the merchant (recipient) email is needed; whatever
  // else was entered is still format-checked.
  if (internalSales) {
    if (!EMAIL_RE.test((input.intakeRecipientEmail ?? "").trim()))
      push(errs, "send-recipientEmail", "Merchant email must be valid.");
    if (businessName && businessName.length < 2)
      push(errs, "businessName", "Business name must be at least 2 characters.");
    if (contactEmail && !EMAIL_RE.test(contactEmail))
      push(errs, "contactEmail", "Merchant contact email must be valid.");
  } else {
    if (businessName.length < 2)
      errs.push({
        field: "businessName",
        message: "Business name is required, even for a draft.",
      });
    if (!EMAIL_RE.test(contactEmail))
      errs.push({
        field: "contactEmail",
        message:
          "A valid merchant contact email is required, even for a draft.",
      });
  }
  salespersonErrors(
    errs,
    input.assignedTechEmail,
    input.assignedToPIN,
    !sendMode,
    ", even for a draft",
  );
  if (ownerTitleMismatch(input.owner.ownershipTypeCd, input.owner.title))
    errs.push({
      field: "owner.title",
      message: ownerTitleMismatchMessage(input.owner.ownershipTypeCd),
    });
  errs.push(
    ...collectPricingOverrideErrors({
      authFeeOverride: input.authFeeOverride,
      annualSecurityBundleFee: input.annualSecurityBundleFee,
      feeOverrides: input.feeOverrides,
      icPlusPricingOverrides: input.icPlusPricingOverrides,
      cloverSecurityAndTransarmor: input.cloverSecurityAndTransarmor,
      fleetOverrides: input.fleetOverrides,
      mnspForm: input.mnspForm,
      mnspEnabled:
        input.mnspEnrollment ||
        input.applicationPath === "MNSP_ENROLLMENT_ONLY",
      terminalRental: input.terminalRental,
      stateCd: input.bizAddr.stateCd,
      monthlyVolume: input.vol.averageMonthlyVolume,
      requireMnspServiceType: !sendMode,
    }),
  );
  return errs;
}

export type CreateValidationInput = {
  applicationPath: string;
  assignedTechEmail: string;
  assignedToPIN: string;
  biz: BusinessForm;
  bizAddr: AddressForm;
  mailAddr: AddressForm;
  mailSame: boolean;
  owner: OwnerForm;
  ownerAddr: AddressForm;
  ownerAddrSame: boolean;
  deposit: BankForm;
  withdrawal: BankForm;
  withdrawalSame: boolean;
  mode: ModeForm;
  vol: VolumeDetailsForms;
  authFeeOverride: string;
  annualSecurityBundleFee: string;
  feeOverrides: FeeOverridesForm;
  icPlusPricingOverrides: IcPlusPricingOverridesForm;
  cloverSecurityAndTransarmor: CloverSecurityAndTransarmorForm;
  fleetOverrides: FleetOverridesForm;
  mnspForm: MnspEnrollmentFormState;
  mnspEnrollment: boolean;
  terminalRental: boolean;
};

export function validateCreateApplication(
  input: CreateValidationInput,
): FieldError[] {
  const errors: FieldError[] = [];
  const boards = input.applicationPath !== "MNSP_ENROLLMENT_ONLY";

  if (!input.assignedTechEmail.trim())
    push(errors, "assigned-tech-email", "Salesperson's email is required.");
  else if (!EMAIL_RE.test(input.assignedTechEmail.trim()))
    push(errors, "assigned-tech-email", "Enter a valid email address.");

  pinError(errors, "assignedToPIN", input.assignedToPIN, { required: true });

  if (!input.biz.businessName.trim())
    push(errors, "businessName", "Business name is required.");
  else if (input.biz.businessName.trim().length < 2)
    push(errors, "businessName", "Business name must be at least 2 characters.");

  requireTaxId(errors, "taxID", input.biz.taxID, input.biz.taxFilingMethod);

  if (!input.biz.contactEmail.trim())
    push(errors, "contactEmail", "Contact email is required.");
  else if (!EMAIL_RE.test(input.biz.contactEmail.trim()))
    push(errors, "contactEmail", "Enter a valid email address.");

  if (!input.biz.contactPhone.trim())
    push(errors, "contactPhone", "Business phone is required.");
  else if (!isValidPhone(input.biz.contactPhone))
    push(errors, "contactPhone", PHONE_MESSAGE);

  if (!input.biz.businessStartDate.trim())
    push(errors, "businessStartDate", "Business start date is required.");
  else {
    const startDateError = dateFieldError(
      input.biz.businessStartDate,
      businessStartDateRules(),
    );
    if (startDateError) push(errors, "businessStartDate", startDateError);
  }

  requireMcc(errors, input.biz.mcc);
  websiteErrors(errors, input.biz.website, input.mode.eCommercePct, true);

  if (boards) errors.push(...volumeTicketErrors(input.vol));

  percentGroupErrors(
    errors,
    "modeOfTransaction",
    "Transaction-mix percentages",
    modeBuckets(input.mode),
  );

  if (!input.owner.firstName.trim())
    push(errors, "owner.firstName", "Owner first name is required.");
  if (!input.owner.lastName.trim())
    push(errors, "owner.lastName", "Owner last name is required.");
  if (boards) {
    if (input.owner.firstName.trim() && ownerNameHasSpace(input.owner.firstName))
      push(errors, "owner.firstName", OWNER_NAME_SINGLE_WORD_MESSAGE);
    if (input.owner.lastName.trim() && ownerNameHasSpace(input.owner.lastName))
      push(errors, "owner.lastName", OWNER_NAME_SINGLE_WORD_MESSAGE);
  }
  if (
    !allowedTitlesForOwnershipType(input.owner.ownershipTypeCd).includes(
      input.owner.title,
    )
  )
    push(
      errors,
      "owner.title",
      ownerTitleMismatchMessage(input.owner.ownershipTypeCd),
    );

  if (!input.owner.email.trim())
    push(errors, "owner.email", "Owner email is required.");
  else if (!EMAIL_RE.test(input.owner.email.trim()))
    push(errors, "owner.email", "Enter a valid email address.");

  if (!input.owner.phone.trim())
    push(errors, "owner.phone", "Owner phone is required.");
  else if (!isValidPhone(input.owner.phone))
    push(errors, "owner.phone", PHONE_MESSAGE);

  ownershipPctError(errors, input.owner.ownershipPct);

  requireSsn(errors, "owner.ssn", input.owner.ssn);

  if (!input.owner.dateOfBirth.trim())
    push(errors, "owner.dateOfBirth", "Owner date of birth is required.");
  else {
    const dobError = dateFieldError(input.owner.dateOfBirth, ownerDobRules());
    if (dobError) push(errors, "owner.dateOfBirth", dobError);
  }

  if (!input.owner.driversLicenseNumber.trim())
    push(
      errors,
      "owner.driversLicenseNumber",
      "Driver's license number is required.",
    );
  if (!input.owner.driversLicenseStateCd.trim())
    push(
      errors,
      "owner.driversLicenseStateCd",
      "Driver's license state is required.",
    );
  else if (!isValidState(input.owner.driversLicenseStateCd))
    push(
      errors,
      "owner.driversLicenseStateCd",
      `Pick a valid state (got "${input.owner.driversLicenseStateCd}").`,
    );

  requireAddress(errors, "biz", input.bizAddr, "Business");
  if (!input.mailSame) requireAddress(errors, "mail", input.mailAddr, "Mailing");
  if (!input.ownerAddrSame)
    requireAddress(errors, "owner", input.ownerAddr, "Owner");

  requireBank(
    errors,
    {
      account: "dep-acct",
      accountConfirm: "dep-acct-confirm",
      routing: "dep-routing",
      type: "dep-type",
      bank: "dep-bank",
    },
    input.deposit,
  );
  if (!input.withdrawalSame)
    requireBank(
      errors,
      {
        account: "wd-acct",
        accountConfirm: "wd-acct-confirm",
        routing: "wd-routing",
        type: "wd-type",
        bank: "wd-bank",
      },
      input.withdrawal,
    );

  errors.push(
    ...collectPricingOverrideErrors({
      authFeeOverride: input.authFeeOverride,
      annualSecurityBundleFee: input.annualSecurityBundleFee,
      feeOverrides: input.feeOverrides,
      icPlusPricingOverrides: input.icPlusPricingOverrides,
      cloverSecurityAndTransarmor: input.cloverSecurityAndTransarmor,
      fleetOverrides: input.fleetOverrides,
      mnspForm: input.mnspForm,
      mnspEnabled:
        input.mnspEnrollment ||
        input.applicationPath === "MNSP_ENROLLMENT_ONLY",
      terminalRental: input.terminalRental,
      stateCd: input.bizAddr.stateCd,
      monthlyVolume: input.vol.averageMonthlyVolume,
    }),
  );

  return errors;
}

export type SendIntakeValidationInput = {
  applicationPath: string;
  assignedTechEmail: string;
  assignedToPIN: string;
  biz: BusinessForm;
  bizAddr: AddressForm;
  mailAddr: AddressForm;
  mailSame: boolean;
  owner: OwnerForm;
  ownerAddr: AddressForm;
  ownerAddrSame: boolean;
  deposit: BankForm;
  withdrawal: BankForm;
  withdrawalSame: boolean;
  mode: ModeForm;
  vol: VolumeDetailsForms;
  sendForm: {
    recipientType: string;
    recipientEmail: string;
    ccEmail: string;
    autoSubmit: boolean;
    expiresInDays: string;
  };
  authFeeOverride: string;
  annualSecurityBundleFee: string;
  feeOverrides: FeeOverridesForm;
  icPlusPricingOverrides: IcPlusPricingOverridesForm;
  cloverSecurityAndTransarmor: CloverSecurityAndTransarmorForm;
  fleetOverrides: FleetOverridesForm;
  mnspForm: MnspEnrollmentFormState;
  mnspEnrollment: boolean;
  terminalRental: boolean;
};

export function validateSendIntake(
  input: SendIntakeValidationInput,
): FieldError[] {
  const errs: FieldError[] = [];
  const bizName = input.biz.businessName.trim();
  const contactEmail = input.biz.contactEmail.trim().toLowerCase();
  const recipientEmail = input.sendForm.recipientEmail.trim().toLowerCase();
  const ccEmail = input.sendForm.ccEmail.trim().toLowerCase();
  // INTERNAL_SALES: PP sales starts it and the merchant finishes it, so only
  // the merchant (recipient) email is required; anything else entered is
  // still format-checked. Every other recipient type needs the business name
  // and merchant contact email to address the invitation.
  const internalSales = input.sendForm.recipientType === "INTERNAL_SALES";
  if (!internalSales || bizName) {
    if (bizName.length < 2)
      push(
        errs,
        "businessName",
        internalSales
          ? "Business name must be at least 2 characters."
          : "Business name is required.",
      );
  }
  if ((!internalSales || contactEmail) && !EMAIL_RE.test(contactEmail))
    push(errs, "contactEmail", "Merchant contact email must be valid.");
  if (!EMAIL_RE.test(recipientEmail))
    push(
      errs,
      "send-recipientEmail",
      internalSales
        ? "Merchant email must be valid."
        : "Recipient email must be valid.",
    );
  if (input.sendForm.recipientType === "BOTH" && !EMAIL_RE.test(ccEmail))
    push(errs, "send-ccEmail", "CC email is required when sending to both.");
  // The salesperson is optional on an intake (assigned later if needed).
  if (input.assignedTechEmail.trim() && !EMAIL_RE.test(input.assignedTechEmail.trim()))
    push(errs, "assigned-tech-email", "Enter a valid email address.");
  if (input.assignedToPIN.trim())
    pinError(errs, "assignedToPIN", input.assignedToPIN, { required: false });

  const expiresDays = parseInt(input.sendForm.expiresInDays, 10);
  if (
    !Number.isFinite(expiresDays) ||
    expiresDays < 1 ||
    expiresDays > 90
  ) {
    push(errs, "send-expires", "Must be between 1 and 90 days.");
  }

  if (input.biz.contactPhone.trim() && !isValidPhone(input.biz.contactPhone))
    push(errs, "contactPhone", PHONE_MESSAGE);
  if (input.biz.businessStartDate.trim()) {
    const startDateError = dateFieldError(
      input.biz.businessStartDate,
      businessStartDateRules(),
    );
    if (startDateError) push(errs, "businessStartDate", startDateError);
  }
  requireTaxId(errs, "taxID", input.biz.taxID, input.biz.taxFilingMethod, {
    required: false,
  });
  if (input.biz.mcc.trim() && !MCC_BY_CODE[input.biz.mcc.trim()])
    push(errs, "mcc", MCC_LIST_MESSAGE);
  websiteErrors(errs, input.biz.website, input.mode.eCommercePct, false);

  if (input.owner.email.trim() && !EMAIL_RE.test(input.owner.email.trim()))
    push(errs, "owner.email", "Enter a valid email address.");
  if (input.owner.phone.trim() && !isValidPhone(input.owner.phone))
    push(errs, "owner.phone", PHONE_MESSAGE);
  if (input.owner.dateOfBirth.trim()) {
    const dobError = dateFieldError(input.owner.dateOfBirth, ownerDobRules());
    if (dobError) push(errs, "owner.dateOfBirth", dobError);
  }
  requireSsn(errs, "owner.ssn", input.owner.ssn, { required: false });
  ownershipPctError(errs, input.owner.ownershipPct);
  if (
    input.owner.driversLicenseStateCd.trim() &&
    !isValidState(input.owner.driversLicenseStateCd)
  ) {
    push(
      errs,
      "owner.driversLicenseStateCd",
      `Pick a valid state (got "${input.owner.driversLicenseStateCd}").`,
    );
  }

  formatAddressIfFilled(errs, "biz", input.bizAddr);
  if (!input.mailSame) formatAddressIfFilled(errs, "mail", input.mailAddr);
  if (!input.ownerAddrSame)
    formatAddressIfFilled(errs, "owner", input.ownerAddr);
  formatRoutingIfFilled(errs, "dep-routing", input.deposit.routingNumber);
  accountConfirmError(
    errs,
    "dep-acct-confirm",
    input.deposit.accountNumber,
    input.deposit.confirmAccountNumber,
  );
  if (!input.withdrawalSame) {
    formatRoutingIfFilled(errs, "wd-routing", input.withdrawal.routingNumber);
    accountConfirmError(
      errs,
      "wd-acct-confirm",
      input.withdrawal.accountNumber,
      input.withdrawal.confirmAccountNumber,
    );
  }

  const modeTouched = modeBuckets(input.mode).some((b) => {
    const n = Number(String(b.value).trim() || 0);
    return (
      !Number.isFinite(n) ||
      n < 0 ||
      n > 100 ||
      (b.field === "modeOfTransaction.swipedPct" ? n !== 100 : n !== 0)
    );
  });
  if (modeTouched) {
    percentGroupErrors(
      errs,
      "modeOfTransaction",
      "Transaction-mix percentages",
      modeBuckets(input.mode),
    );
  }

  if (
    input.sendForm.autoSubmit &&
    input.applicationPath !== "MNSP_ENROLLMENT_ONLY"
  ) {
    errs.push(...volumeTicketErrors(input.vol));
    if (input.owner.firstName.trim() && ownerNameHasSpace(input.owner.firstName))
      push(errs, "owner.firstName", OWNER_NAME_SINGLE_WORD_MESSAGE);
    if (input.owner.lastName.trim() && ownerNameHasSpace(input.owner.lastName))
      push(errs, "owner.lastName", OWNER_NAME_SINGLE_WORD_MESSAGE);
  }
  if (ownerTitleMismatch(input.owner.ownershipTypeCd, input.owner.title))
    push(
      errs,
      "owner.title",
      ownerTitleMismatchMessage(input.owner.ownershipTypeCd),
    );
  errs.push(
    ...collectPricingOverrideErrors({
      authFeeOverride: input.authFeeOverride,
      annualSecurityBundleFee: input.annualSecurityBundleFee,
      feeOverrides: input.feeOverrides,
      icPlusPricingOverrides: input.icPlusPricingOverrides,
      cloverSecurityAndTransarmor: input.cloverSecurityAndTransarmor,
      fleetOverrides: input.fleetOverrides,
      mnspForm: input.mnspForm,
      mnspEnabled:
        input.mnspEnrollment ||
        input.applicationPath === "MNSP_ENROLLMENT_ONLY",
      terminalRental: input.terminalRental,
      stateCd: input.bizAddr.stateCd,
      monthlyVolume: input.vol.averageMonthlyVolume,
      // The recipient picks the register; intake completion requires it.
      requireMnspServiceType: false,
    }),
  );
  return errs;
}

export type IntakeValidationInput = {
  form: {
    businessName: string;
    businessStartDate: string;
    taxID: string | null;
    taxFilingMethod: string;
    contactEmail: string;
    contactPhone: string;
    website: string;
    mcc: string;
    modeOfTransaction: ModeForm;
    volumeDetails: VolumeDetailsForms;
    owner: {
      firstName: string;
      lastName: string;
      title: string;
      email: string;
      phone: string;
      ssn: string | null;
      dateOfBirth: string;
      ownershipTypeCd: string;
      ownershipPct: string;
      driversLicenseNumber: string | null;
      driversLicenseStateCd: string;
      address: AddressLike;
    };
    businessAddress: AddressLike;
    mailingAddress: AddressLike;
    bankDetail: { deposit: BankLike; withdrawal?: BankLike | null };
  };
  mailingSameAsBusiness: boolean;
  withdrawalSameAsDeposit: boolean;
  enrollmentOnly: boolean;
  isCensored: (field: string) => boolean;
};

export function validateIntakeSubmit(
  input: IntakeValidationInput,
): FieldError[] {
  const errors: FieldError[] = [];
  const { form } = input;

  if (!form.businessName.trim())
    push(errors, "businessName", "Business name is required.");
  else if (form.businessName.trim().length < 2)
    push(errors, "businessName", "Business name must be at least 2 characters.");

  if (!form.businessStartDate.trim())
    push(errors, "businessStartDate", "Business start date is required.");
  else {
    const startDateError = dateFieldError(
      form.businessStartDate,
      businessStartDateRules(),
    );
    if (startDateError) push(errors, "businessStartDate", startDateError);
  }

  requireTaxId(errors, "taxID", form.taxID, form.taxFilingMethod, {
    censored: input.isCensored("taxID"),
  });

  if (!form.contactEmail.trim())
    push(errors, "contactEmail", "Contact email is required.");
  else if (!EMAIL_RE.test(form.contactEmail.trim()))
    push(errors, "contactEmail", "Enter a valid email address.");

  if (!form.contactPhone.trim())
    push(errors, "contactPhone", "Business phone is required.");
  else if (!isValidPhone(form.contactPhone))
    push(errors, "contactPhone", PHONE_MESSAGE);

  websiteErrors(
    errors,
    form.website,
    form.modeOfTransaction.eCommercePct,
    true,
  );

  percentGroupErrors(
    errors,
    "modeOfTransaction",
    "Transaction-mix percentages",
    modeBuckets(form.modeOfTransaction),
  );

  if (!form.owner.firstName.trim())
    push(errors, "owner.firstName", "Owner first name is required.");
  if (!form.owner.lastName.trim())
    push(errors, "owner.lastName", "Owner last name is required.");
  if (!input.enrollmentOnly) {
    if (form.owner.firstName.trim() && ownerNameHasSpace(form.owner.firstName))
      push(errors, "owner.firstName", OWNER_NAME_SINGLE_WORD_MESSAGE);
    if (form.owner.lastName.trim() && ownerNameHasSpace(form.owner.lastName))
      push(errors, "owner.lastName", OWNER_NAME_SINGLE_WORD_MESSAGE);
  }
  const allowedTitles = allowedTitlesForOwnershipType(
    form.owner.ownershipTypeCd,
  );
  if (!allowedTitles.includes(form.owner.title)) {
    const titles = allowedTitles.length
      ? allowedTitles.join(", ")
      : "(unknown ownership type)";
    push(
      errors,
      "owner.title",
      `Pick a title that matches the ownership type. Allowed: ${titles}.`,
    );
  }

  if (!form.owner.email.trim())
    push(errors, "owner.email", "Owner email is required.");
  else if (!EMAIL_RE.test(form.owner.email.trim()))
    push(errors, "owner.email", "Enter a valid email address.");

  if (!form.owner.phone.trim())
    push(errors, "owner.phone", "Owner phone is required.");
  else if (!isValidPhone(form.owner.phone))
    push(errors, "owner.phone", PHONE_MESSAGE);

  ownershipPctError(errors, form.owner.ownershipPct);

  requireSsn(errors, "owner.ssn", form.owner.ssn, {
    censored: input.isCensored("ssn"),
  });

  if (!form.owner.dateOfBirth.trim())
    push(errors, "owner.dateOfBirth", "Owner date of birth is required.");
  else {
    const dobError = dateFieldError(form.owner.dateOfBirth, ownerDobRules());
    if (dobError) push(errors, "owner.dateOfBirth", dobError);
  }

  if (!form.owner.driversLicenseNumber?.trim())
    push(
      errors,
      "owner.driversLicenseNumber",
      "Driver's license number is required.",
    );
  if (!form.owner.driversLicenseStateCd.trim())
    push(
      errors,
      "owner.driversLicenseStateCd",
      "Driver's license state is required.",
    );

  if (!input.enrollmentOnly)
    errors.push(...volumeTicketErrors(form.volumeDetails));
  requireMcc(errors, form.mcc);

  requireAddress(errors, "businessAddress", form.businessAddress, "Business");
  if (!input.mailingSameAsBusiness)
    requireAddress(errors, "mailingAddress", form.mailingAddress, "Mailing");
  requireAddress(errors, "owner.address", form.owner.address, "Owner");
  if (
    form.owner.driversLicenseStateCd &&
    !isValidState(form.owner.driversLicenseStateCd)
  ) {
    push(
      errors,
      "owner.driversLicenseStateCd",
      `Pick a valid state (got "${form.owner.driversLicenseStateCd}").`,
    );
  }

  requireBank(
    errors,
    {
      account: "bankDetail.deposit.accountNumber",
      accountConfirm: "bankDetail.deposit.confirmAccountNumber",
      routing: "bankDetail.deposit.routingNumber",
      type: "bankDetail.deposit.accountType",
      bank: "bankDetail.deposit.bankName",
    },
    form.bankDetail.deposit,
    {
      routingCensored: input.isCensored("depositRoutingNumber"),
      accountCensored: input.isCensored("depositAccountNumber"),
    },
  );
  if (!input.withdrawalSameAsDeposit && form.bankDetail.withdrawal) {
    requireBank(
      errors,
      {
        account: "bankDetail.withdrawal.accountNumber",
        accountConfirm: "bankDetail.withdrawal.confirmAccountNumber",
        routing: "bankDetail.withdrawal.routingNumber",
        type: "bankDetail.withdrawal.accountType",
        bank: "bankDetail.withdrawal.bankName",
      },
      form.bankDetail.withdrawal,
      {
        routingCensored: input.isCensored("withdrawalRoutingNumber"),
        accountCensored: input.isCensored("withdrawalAccountNumber"),
      },
    );
  }

  return errors;
}

export function countIntakeMnspMissing(input: {
  mnspRequested: boolean;
  terminalRentalRequested: boolean;
  mnspForm: MnspEnrollmentFormState;
  hasDriversLicense: boolean;
  hasVoidedCheck: boolean;
}): number {
  if (!(input.mnspRequested || input.terminalRentalRequested)) return 0;
  return (
    findMissingAcceptanceLabels(
      input.mnspForm,
      input.mnspRequested,
      input.terminalRentalRequested,
    ).length +
    mnspServiceTypeErrors(input.mnspForm, input.mnspRequested).length +
    (input.mnspForm.acceptedBy.trim() ? 0 : 1) +
    (input.mnspForm.signatureImage ? 0 : 1) +
    (input.hasDriversLicense ? 0 : 1) +
    (input.hasVoidedCheck ? 0 : 1)
  );
}

export function getIntakeSubmitRemaining(
  input: IntakeValidationInput & {
    mnspRequested: boolean;
    terminalRentalRequested: boolean;
    mnspForm: MnspEnrollmentFormState;
    hasDriversLicense: boolean;
    hasVoidedCheck: boolean;
  },
): number {
  return (
    validateIntakeSubmit(input).length +
    countIntakeMnspMissing(input)
  );
}

export type EditValidationInput = {
  businessName: string;
  merchantEmail: string;
  templateID: string;
  assignedToPIN: string;
  assignedTechEmail: string;
  applicationPath: string;
  needsIdentityReentry: boolean;
  owner: Pick<
    OwnerForm,
    "firstName" | "lastName" | "email" | "ownershipTypeCd" | "title" | "ssn"
  >;
  bizAddr: AddressForm;
  mailAddr: AddressForm;
  mailSame: boolean;
  ownerAddr: AddressForm;
  ownerAddrSame: boolean;
  taxID: string;
  deposit: BankForm;
  withdrawal: BankForm;
  withdrawalSame: boolean;
  authFeeOverride: string;
  annualSecurityBundleFee: string;
  feeOverrides: FeeOverridesForm;
  icPlusPricingOverrides: IcPlusPricingOverridesForm;
  cloverSecurityAndTransarmor: CloverSecurityAndTransarmorForm;
  fleetOverrides: FleetOverridesForm;
  mnspForm: MnspEnrollmentFormState;
  mnspEnrollmentRequested: boolean;
  terminalRentalRequested: boolean;
  // Business state + monthly volume drive the state ETF-cap check (mirrors the
  // server's boarding guard), so an operator cannot save/send an over-cap
  // application from the edit modal.
  stateCd: string;
  monthlyVolume: string;
};

export function validateEditApplication(
  input: EditValidationInput,
  action: CardConnectEditAction = "NONE",
): Record<string, string> {
  const next: Record<string, string> = {};
  if (input.businessName.trim().length < 2)
    next.businessName = "Legal business name is required.";
  if (!EMAIL_RE.test(input.merchantEmail.trim()))
    next.merchantEmail = "Must be a valid email address.";
  if (!/^\d+$/.test(input.templateID.trim()))
    next.templateID = "Template ID must be a numeric value.";
  if (input.assignedToPIN && !/^\d{4,8}$/.test(input.assignedToPIN))
    next.assignedToPIN = "Must be a 4-8 digit number.";
  if (input.assignedTechEmail && !EMAIL_RE.test(input.assignedTechEmail.trim()))
    next.assignedTechEmail = "Must be a valid email address.";
  if (input.owner.email && !EMAIL_RE.test(input.owner.email.trim()))
    next["owner.email"] = "Must be a valid email address.";

  if (input.applicationPath !== "MNSP_ENROLLMENT_ONLY") {
    if (input.owner.firstName.trim() && ownerNameHasSpace(input.owner.firstName))
      next["owner.firstName"] = OWNER_NAME_SINGLE_WORD_MESSAGE;
    if (input.owner.lastName.trim() && ownerNameHasSpace(input.owner.lastName))
      next["owner.lastName"] = OWNER_NAME_SINGLE_WORD_MESSAGE;
  }
  if (ownerTitleMismatch(input.owner.ownershipTypeCd, input.owner.title))
    next["owner.title"] = ownerTitleMismatchMessage(
      input.owner.ownershipTypeCd,
    );

  // Addresses are optional here (this is an edit, not first entry) so only
  // format-if-filled applies -- same rules as the send-intake prefill. A ZIP
  // that IS filled in still has to be at least 5 digits, same as everywhere
  // else the field appears.
  const addrErrs: FieldError[] = [];
  formatAddressIfFilled(addrErrs, "biz", input.bizAddr);
  if (!input.mailSame) formatAddressIfFilled(addrErrs, "mail", input.mailAddr);
  if (!input.ownerAddrSame)
    formatAddressIfFilled(addrErrs, "owner", input.ownerAddr);
  for (const { field, message } of addrErrs) next[field] = message;

  const identityReentryRequired =
    action === "SUBMIT" && input.needsIdentityReentry;
  if (!input.taxID && identityReentryRequired) {
    next.taxID =
      "Required to retry — this application never reached CardConnect.";
  } else if (input.taxID && !TAX_ID_RE.test(input.taxID.replace(/\D/g, ""))) {
    next.taxID = "Tax ID must be exactly 9 digits.";
  }
  if (!input.owner.ssn && identityReentryRequired) {
    next["owner.ssn"] =
      "Required to retry — this application never reached CardConnect.";
  } else if (input.owner.ssn && !SSN_RE.test(input.owner.ssn)) {
    next["owner.ssn"] = "Enter a valid SSN.";
  }
  if (!input.deposit.accountNumber && identityReentryRequired) {
    next["dep-acct"] =
      "Required to retry — this application never reached CardConnect.";
  }
  if (!input.deposit.routingNumber && identityReentryRequired) {
    next["dep-routing"] =
      "Required to retry — this application never reached CardConnect.";
  } else if (
    input.deposit.routingNumber &&
    (!ROUTING_RE.test(input.deposit.routingNumber) ||
      !isValidRoutingNumber(input.deposit.routingNumber))
  ) {
    next["dep-routing"] = "Enter a valid 9-digit routing number.";
  }
  if (
    !input.withdrawalSame &&
    input.withdrawal.routingNumber &&
    (!ROUTING_RE.test(input.withdrawal.routingNumber) ||
      !isValidRoutingNumber(input.withdrawal.routingNumber))
  ) {
    next["wd-routing"] = "Enter a valid 9-digit routing number.";
  }

  // Double-entry account match (issue #195). Account numbers hydrate blank on
  // an edit and are only re-keyed when the operator opens the sensitive
  // fields, so the match is enforced only when a real account number is being
  // entered -- accountConfirmError skips a blank or censored value.
  const bankConfirmErrs: FieldError[] = [];
  accountConfirmError(
    bankConfirmErrs,
    "dep-acct-confirm",
    input.deposit.accountNumber,
    input.deposit.confirmAccountNumber,
  );
  if (!input.withdrawalSame)
    accountConfirmError(
      bankConfirmErrs,
      "wd-acct-confirm",
      input.withdrawal.accountNumber,
      input.withdrawal.confirmAccountNumber,
    );
  for (const { field, message } of bankConfirmErrs) next[field] = message;

  if (authFeeOverrideInvalid(input.authFeeOverride))
    next["fee-override.authFee"] = authFeeOverrideErrorMessage(
      input.authFeeOverride,
    );
  if (annualSecurityBundleFeeInvalid(input.annualSecurityBundleFee))
    next["fee-override.annualSecurityBundleFee"] =
      annualSecurityBundleFeeErrorMessage(input.annualSecurityBundleFee);
  const etfCapErr = earlyCancelFeeCapInvalidField(
    input.feeOverrides,
    input.stateCd,
    input.monthlyVolume,
    "fee-override",
  );
  if (etfCapErr) next[etfCapErr.field] = etfCapErr.message;
  for (const { field, message } of [
    ...feeOverridesInvalidFields(input.feeOverrides, "fee-override"),
    ...icPlusPricingOverridesInvalidFields(
      input.icPlusPricingOverrides,
      "fee-override",
    ),
    ...cloverSecurityAndTransarmorInvalidFields(
      input.cloverSecurityAndTransarmor,
      "clover-security",
    ),
    ...fleetOverridesInvalidFields(input.fleetOverrides, "fleet-override"),
    ...mnspFeeFieldErrors(
      input.mnspForm,
      input.mnspEnrollmentRequested,
      input.terminalRentalRequested,
    ),
    ...mnspServiceTypeErrors(
      input.mnspForm,
      input.mnspEnrollmentRequested,
    ),
  ]) {
    next[field] = message;
  }
  return next;
}
